import { IconLocationGroup } from '@assets/icons'
import { Box, Flex, Text } from '@chakra-ui/react'
import type React from 'react'
import { MdOutlineLocationOn } from 'react-icons/md'
import { useLocationsContext } from '../contexts/LocationsContext'
import { appColors } from '../theme/foundations/colors'

export type LocationBadgeLabelTypography = {
  fontFamily?: string
  fontSize?: string | number
  fontStyle?: React.CSSProperties['fontStyle']
  fontWeight?: string | number
  lineHeight?: string | number
}

type LocationBadgeProps = {
  showGroupIcon?: boolean
  buildingId?: number
  color?: string
  border?: string
  borderRadius?: string
  height?: string
  labelTypography?: LocationBadgeLabelTypography
} & ({ locationId: number } | { locationName: string } | { locationFirebaseId?: string })

/**
 * Renders a badge displaying a location's name and icon.
 * The component determines the location's name by either directly using `locationName` from props,
 * or by retrieving the corresponding name using `locationId` from a context containing all locations.
 * It supports displaying one of two icons next to the location name depending on the `showGroupIcon` prop.
 *
 * @param {Object} props - The props for the LocationBadge component.
 * @param {number} [props.locationId] - This is locationPgId. Optional.
 * @param {string} [props.locationName] - Optional name of the location to display directly.
 * @param {boolean} [props.showGroupIcon=false] - If true, displays a group icon, otherwise displays location icon.
 * @param {LocationBadgeLabelTypography} [props.labelTypography] - Optional `fontFamily`, `fontSize`, `fontStyle`, `fontWeight` for the label.
 * @returns {JSX.Element} A styled box component containing the location name and an icon.
 */
export const LocationBadge: React.FC<LocationBadgeProps> = props => {
  const { allLocations } = useLocationsContext()
  const locationPgId = 'locationId' in props ? props.locationId : undefined
  const locationFirebaseId = 'locationFirebaseId' in props ? props.locationFirebaseId : undefined
  const locationNameProp = 'locationName' in props ? props.locationName : undefined

  const location = locationPgId
    ? allLocations.find(l => l.locationPgId === locationPgId)
    : locationFirebaseId
      ? allLocations.find(l => l.id === locationFirebaseId)
      : undefined
  const locationName = location?.name ?? locationNameProp

  const building = props.buildingId ? location?.buildings.find(b => b.id === props.buildingId) : undefined
  const buildingName = building?.name

  const labelTextProps = {
    fontSize: '12px' as const,
    fontWeight: 500 as const,
    ...props.labelTypography,
  }

  const icon = props.showGroupIcon ? <IconLocationGroup /> : <MdOutlineLocationOn size={13} color={props.color ?? appColors.secondary} />

  return (
    <Box
      p='4px 8px'
      border={props.border}
      borderRadius={props.borderRadius ?? '6px'}
      bg={appColors.muted}
      h={props.height ?? '32px'}
      w='min-content'
      maxW='full'
    >
      <Flex color={props.color ?? appColors.foreground} h='full' justifyContent='center' alignItems='center' gap='4px'>
        {icon}
        <Text {...labelTextProps} isTruncated>
          {locationName}
          {buildingName ? `, ${buildingName}` : ''}
        </Text>
      </Flex>
    </Box>
  )
}
